// src/components/sourcex/VersionRedlineModal.jsx
//
// What changed in a visit around a chosen version — the marked-up read a
// reviewer does before signing off on a version, or before deciding whether an
// old one is worth restoring.
//
// Two comparisons, because there are two real questions and one answer cannot
// serve both:
//
//   "previous" (the default) — PREVIOUS APPROVED VERSION -> THIS VERSION.
//       What this numbered version changed. Version 2.0's list is the work
//       done since Version 1.0 — what the approved document itself claims, and
//       what a reviewer holding it is checking.
//   "now"                    — THIS VERSION -> THE VISIT AS IT STANDS NOW.
//       What has happened since, which is the question someone deciding
//       whether to restore an old version has. Without it, edits made after
//       the last approval would be invisible here.
//
// Both compare snapshots of THE SAME VISIT only — the baseline comes from
// previousApprovedVersion(), which is scoped by eventId. See templateRedline.js.
//
// Which one is on screen is spelled out in the subheading, never implied: the
// same list of changes means different things under the two baselines, and a
// reviewer reading the wrong one signs off on the wrong thing.
//
// It renders each question through SourcePreviewField — the same component the
// document and the export use — so the redline shows the FORM changing, not a
// diff of a data structure. A reviewer signing off on a source document needs
// to see the printed field, not `{type: "Select", options: [...]}`.
//
// Presentation only. Nothing here writes.

import { useState } from "react";
import { GitCompare, X, Plus, Minus, Pencil, ArrowUpDown } from "lucide-react";
import SourcePreviewField from "./SourcePreviewField";
import {
  buildVisitRedline,
  groupRedlineByHeading,
  describeRedline,
  redlineIsEmpty,
  versionLabel,
} from "../../services/templateRedline";

const STATUS = {
  added: {
    label: "Added",
    icon: Plus,
    chip: "text-emerald-200 bg-emerald-400/15 border-emerald-400/30",
    card: "border-emerald-400/40 bg-emerald-400/[0.06]",
  },
  removed: {
    label: "Removed",
    icon: Minus,
    chip: "text-rose-200 bg-rose-400/15 border-rose-400/30",
    card: "border-rose-400/40 bg-rose-500/[0.07]",
  },
  changed: {
    label: "Changed",
    icon: Pencil,
    chip: "text-amber-200 bg-amber-400/15 border-amber-400/30",
    card: "border-amber-400/40 bg-amber-400/[0.06]",
  },
  moved: {
    label: "Moved",
    icon: ArrowUpDown,
    chip: "text-sky-200 bg-sky-400/15 border-sky-400/30",
    card: "border-sky-400/35 bg-sky-400/[0.05]",
  },
  unchanged: {
    label: "",
    icon: null,
    chip: "",
    card: "border-white/10 bg-white/[0.02]",
  },
};

const formatWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso || ""
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/**
 * @param {object} version           the snapshot being read
 * @param {Array}  versionQuestions  that snapshot's questions
 * @param {object} [previousVersion] the previous APPROVED version of the same
 *   visit, or null when there is none (a visit's first approval, or a
 *   built/backup snapshot taken before any approval)
 * @param {Array}  [previousQuestions]  that baseline's questions
 * @param {Array}  [currentQuestions]   the visit's questions right now
 * @param {number} [versionNumber]   this snapshot's "Version N.0", 0 if unnumbered
 * @param {number} [previousNumber]  the baseline's, same rule
 * @param {string} eventName
 * @param {Map}    [labelById]   for rendering a condition's "only if …"
 * @param {() => void} onClose
 */
export default function VersionRedlineModal({
  version,
  versionQuestions = [],
  previousVersion = null,
  previousQuestions = [],
  currentQuestions = [],
  versionNumber = 0,
  previousNumber = 0,
  eventName = "",
  labelById,
  onClose,
}) {
  // Opens on "what did this version change", except for a version with nothing
  // before it — there the previous-version view has nothing to say, so the
  // modal opens on the one that does rather than on an empty answer.
  const [mode, setMode] = useState(previousVersion ? "previous" : "now");
  const showingPrevious = mode === "previous" && !!previousVersion;

  const { rows, summary } = showingPrevious
    ? buildVisitRedline(previousQuestions, versionQuestions, labelById)
    : buildVisitRedline(versionQuestions, currentQuestions, labelById);

  // How each side is named. The version number is what the exported document
  // carries, so it is what a reviewer recognises; a snapshot with no number
  // (built/backup) falls back to when it was taken.
  const nameOf = (v, n) => versionLabel(n) || `the version saved ${formatWhen(v?.createdAt)}`;
  const thisName = nameOf(version, versionNumber);
  const prevName = nameOf(previousVersion, previousNumber);

  const tab = (key, label, disabled, title) => (
    <button
      key={key}
      onClick={() => setMode(key)}
      disabled={disabled}
      title={title}
      className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
        (key === "previous") === showingPrevious
          ? "bg-sky-400/20 border border-sky-300/40 text-sky-100"
          : "bg-white/[0.04] border border-white/10 text-slate-400 hover:text-slate-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl max-h-[88vh] flex flex-col rounded-2xl border border-white/10 bg-[#0c0726] shadow-2xl">
        <div className="shrink-0 px-5 pt-5 pb-3 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
                <GitCompare size={18} />{" "}
                {showingPrevious
                  ? `What changed in ${thisName}`
                  : "What changed since this version"}
              </h2>
              {/* Spelled out, not implied. "What changed" on its own leaves a
                  reader guessing whether it means since the version before it,
                  the version after it, or today — and the answer decides
                  whether the list can be trusted. */}
              <p className="mt-0.5 text-xs text-slate-400">
                Everything done to{" "}
                <span className="text-slate-300">{eventName || "this visit"}</span>{" "}
                between{" "}
                <span className="text-slate-300">
                  {showingPrevious ? prevName : thisName}
                </span>{" "}
                and{" "}
                <span className="text-slate-300">
                  {showingPrevious ? thisName : "the visit as it stands now"}
                </span>
                .
              </p>
              {!previousVersion && (
                // Said plainly rather than shown as an empty diff: "no changes"
                // and "nothing came before this" look identical in a redline
                // and mean completely different things.
                <p className="mt-1 text-[11px] text-amber-200/80">
                  {version?.kind === "approved"
                    ? `This is the first approved version of ${
                        eventName || "this visit"
                      } — there is nothing before it to compare against.`
                    : `No approved version of ${
                        eventName || "this visit"
                      } was taken before this snapshot, so there is nothing to compare it against.`}{" "}
                  Showing what has changed since instead.
                </p>
              )}
              <p className="mt-0.5 text-[11px] text-slate-500">
                Struck-through is what the old version had; highlighted is what
                replaced it. Nothing here changes anything — use Restore to put
                the old version back.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
              <X size={18} />
            </button>
          </div>
          {/* Which comparison is on screen, switchable. Reading an old version
              to decide whether to restore it is a different job from checking
              what a version changed, and both are done from this same list. */}
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-0.5">
              Compare
            </span>
            {tab(
              "previous",
              previousVersion ? `${prevName} → ${thisName}` : "Previous version",
              !previousVersion,
              previousVersion
                ? `What ${thisName} changed`
                : "Nothing approved came before this snapshot"
            )}
            {tab("now", `${thisName} → now`, false, "What has changed since this version")}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {redlineIsEmpty(summary) ? (
              <span className="text-xs text-slate-400">
                {showingPrevious
                  ? `${thisName} changed nothing — it is identical to ${prevName}.`
                  : "Nothing has changed since this version."}
              </span>
            ) : (
              ["added", "removed", "changed", "moved"]
                .filter((k) => summary[k] > 0)
                .map((k) => {
                  const S = STATUS[k];
                  const Icon = S.icon;
                  return (
                    <span
                      key={k}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${S.chip}`}
                    >
                      <Icon className="w-3 h-3" />
                      {summary[k]} {S.label}
                    </span>
                  );
                })
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4 space-y-4">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              This version has no questions in it.
            </p>
          ) : (
            groupRedlineByHeading(rows).map((g, gi) => {
              const HS = STATUS[g.heading?.status] || STATUS.unchanged;
              const HIcon = HS.icon;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <section key={`${g.heading?.question?.id || "ungrouped"}-${gi}`}>
                  {/* The section band, so the comparison reads the way the form
                      does. A heading is a row like any other, so it carries its
                      own status too — a renamed section shows as changed here
                      as well as grouping what sits under it. */}
                  <div
                    className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2 ${
                      g.heading ? HS.card : "border-white/20 bg-white/[0.04]"
                    }`}
                  >
                    <span
                      className={`text-sm font-bold uppercase tracking-wider min-w-0 ${
                        g.heading?.status === "removed"
                          ? "text-rose-200 line-through"
                          : "text-white"
                      }`}
                    >
                      {g.heading
                        ? g.heading.question?.question || "(untitled section)"
                        : "Before the first heading"}
                    </span>
                    {g.heading && HIcon && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${HS.chip}`}
                      >
                        <HIcon className="w-3 h-3" />
                        {HS.label}
                      </span>
                    )}
                    <span className="ml-auto shrink-0 text-[11px] text-slate-500 tabular-nums">
                      {g.changed
                        ? `${g.changed} change${g.changed === 1 ? "" : "s"}`
                        : "unchanged"}
                    </span>
                  </div>

                  <div className="mt-2 space-y-2 pl-2">
                    {g.rows.length === 0 && (
                      <p className="text-[11px] text-slate-500 italic">
                        No questions in this section.
                      </p>
                    )}
                    {g.rows.map((r, i) => {
              const S = STATUS[r.status] || STATUS.unchanged;
              const Icon = S.icon;
              const removed = r.status === "removed";
              return (
                <div
                  // Rows are positional — a question can appear once as removed
                  // and the list is rebuilt on every render.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${r.question?.id || "row"}-${i}`}
                  className={`rounded-xl border px-3 py-2.5 ${S.card}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`font-medium min-w-0 ${
                        removed ? "text-rose-200 line-through" : "text-slate-100"
                      }`}
                    >
                      {r.question?.question || (
                        <span className="italic text-slate-500">(no question text)</span>
                      )}
                      {r.question?.required && (
                        <span className="ml-1 text-rose-300">*</span>
                      )}
                    </p>
                    {Icon && (
                      <span
                        className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${S.chip}`}
                      >
                        <Icon className="w-3 h-3" />
                        {S.label}
                      </span>
                    )}
                  </div>

                  {/* Field-level detail for a changed question — the old value
                      struck through beside the new one, so the change reads at
                      a glance without opening the editor. */}
                  {r.status === "changed" && (
                    <ul className="mt-1.5 space-y-0.5">
                      {r.changes.map((c) => (
                        <li key={c.field} className="text-[11px] text-amber-100/90">
                          <span className="text-amber-300/80">{c.label}: </span>
                          <span className="line-through text-amber-200/60">{c.from}</span>
                          <span className="mx-1 text-amber-300/70">→</span>
                          <span className="font-medium">{c.to}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* The printed field itself. A removed one is dimmed and
                      struck through above; showing it still lets a reviewer
                      see exactly what came out of the form. */}
                  <div
                    className={
                      removed ? "opacity-60 mt-1" : "mt-1"
                    }
                  >
                    <SourcePreviewField
                      question={r.question}
                      labelById={labelById}
                      hideConditionNote
                    />
                  </div>
                </div>
              );
                    })}
                  </div>
                </section>
              );
            })
          )}
        </div>

        <div className="shrink-0 px-5 py-3 border-t border-white/10 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">
            {describeRedline(summary)}
          </span>
          <button
            onClick={onClose}
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
